import { Stack } from '@chakra-ui/react';
import type { KeyboardEvent } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { ProductPage } from 'types/cms/pages/ProductPage';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';

import { SkincareVariantRadioItem } from './SkincareVariantRadioItem';

interface SkincareFormatRadioGroupProps {
  productFormats: NonNullable<ProductPage['productFormats']>;
  shopifyProducts: (ShopifyProduct | null | undefined)[];
  selectedSlug: string;
  onSelect: (slug: string) => void;
  onOptionHover?: (slug: string) => void;
  isDiscountHidden?: boolean;
}

export const SkincareFormatRadioGroup = ({
  productFormats,
  shopifyProducts,
  selectedSlug,
  onSelect,
  onOptionHover,
  isDiscountHidden,
}: SkincareFormatRadioGroupProps) => {
  const variantRadioItemRefs = useRef<(HTMLDivElement | null)[]>([]);

  /* INFO: This is to handle keyboard navigation on the radio group as per
   * the  WAI-ARIA radiogroup pattern https://www.w3.org/WAI/ARIA/apg/patterns/radio/
   */
  const handleRadioGroupKeyDown = useCallback((event: KeyboardEvent) => {
    const { key } = event;

    if (
      key !== 'ArrowRight' &&
      key !== 'ArrowDown' &&
      key !== 'ArrowLeft' &&
      key !== 'ArrowUp'
    ) {
      return;
    }

    event.preventDefault();

    const currentIndex = variantRadioItemRefs.current.findIndex(
      (ref) => ref === document.activeElement,
    );
    if (currentIndex === -1) return;

    const count = variantRadioItemRefs.current.length;
    const nextIndex =
      key === 'ArrowRight' || key === 'ArrowDown'
        ? (currentIndex + 1) % count
        : (currentIndex - 1 + count) % count;

    const nextRadio = variantRadioItemRefs.current[nextIndex];
    if (nextRadio) {
      nextRadio.focus();
      nextRadio.click();
    }
  }, []);

  const options = useMemo(
    () =>
      productFormats.map(
        ({ product: productFormat, variant: chosenVariant }) => {
          const product = shopifyProducts.find((p) =>
            p?.variants.some((v) => v.id === chosenVariant.gid),
          );
          const variant = product?.variants.find(
            (v) => v.id === chosenVariant.gid,
          );

          return {
            label: productFormat.formatLabel,
            slug: productFormat.slug,
            price: variant?.price ?? null,
            compareAtPrice: variant?.compareAtPrice ?? null,
            netContents: variant?.netContents ?? '',
          };
        },
      ),
    [productFormats, shopifyProducts],
  );

  return (
    <Stack
      spacing="4xs"
      role="radiogroup"
      aria-label="Select size"
      onKeyDown={handleRadioGroupKeyDown}
    >
      {options.map((option, index) => {
        const isSelected = selectedSlug === option.slug;

        return (
          <SkincareVariantRadioItem
            key={option.slug}
            ref={(el) => {
              variantRadioItemRefs.current[index] = el;
            }}
            label={option.label}
            netContents={option.netContents}
            isSelected={isSelected}
            price={option.price}
            compareAtPrice={
              isDiscountHidden ? undefined : option.compareAtPrice
            }
            onMouseEnter={() => {
              if (!isSelected) onOptionHover?.(option.slug);
            }}
            onClick={() => {
              if (!isSelected) onSelect(option.slug);
            }}
          />
        );
      })}
    </Stack>
  );
};
